import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { FileUpload } from "@/components/FileUpload";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Lock, Loader2, History, Pencil, Repeat } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["available", "rented", "service", "recovery"];
const EMPTY = {
  reg_number: "", model: "", type: "scooter", vehicle_value: 120000, status: "available",
  color: "", model_year: "", registration_date: "", insurance_expiry: "", last_service_date: "",
  battery_number: "", charger_number: "", battery_image: null, charger_image: null, notes: "",
};

function VehicleDialog({ mode = "add", vehicle, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && mode === "edit" && vehicle) {
      setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, vehicle[k] ?? EMPTY[k]])) });
    } else if (open) setForm(EMPTY);
  }, [open, mode, vehicle]);

  const submit = async () => {
    if (!form.reg_number || !form.model) return toast.error("Registration & model are required");
    setSaving(true);
    const payload = {
      ...form,
      vehicle_value: Number(form.vehicle_value) || 0,
      model_year: form.model_year ? Number(form.model_year) : null,
    };
    try {
      if (mode === "edit") await api.patch(`/operator/vehicles/${vehicle.id}`, payload);
      else await api.post("/operator/vehicles", payload);
      toast.success(mode === "edit" ? "Vehicle updated" : "Vehicle added");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {mode === "edit"
          ? <Button data-testid={`edit-vehicle-${vehicle.reg_number}`} variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
          : <Button data-testid="add-vehicle-btn" size="sm" className="gap-1.5"><Plus className="h-4 w-4" /> Add vehicle</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{mode === "edit" ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
          <DialogDescription>Vehicle details, condition and documentation.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Registration number</Label><Input data-testid="vehicle-reg" value={form.reg_number} onChange={(e) => set("reg_number", e.target.value)} placeholder="PY01XX0000" /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input data-testid="vehicle-model" value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Ather 450X" /></div>
            <div className="space-y-1.5"><Label>Type</Label><Input value={form.type} onChange={(e) => set("type", e.target.value)} placeholder="scooter" /></div>
            <div className="space-y-1.5"><Label>Vehicle value (₹)</Label><Input data-testid="vehicle-value" type="number" value={form.vehicle_value} onChange={(e) => set("vehicle_value", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="vehicle-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Colour</Label><Input data-testid="vehicle-color" value={form.color} onChange={(e) => set("color", e.target.value)} placeholder="Pearl White" /></div>
            <div className="space-y-1.5"><Label>Model year</Label><Input type="number" value={form.model_year} onChange={(e) => set("model_year", e.target.value)} placeholder="2024" /></div>
            <div className="space-y-1.5"><Label>Registration date</Label><Input type="date" value={form.registration_date || ""} onChange={(e) => set("registration_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Insurance expiry</Label><Input data-testid="vehicle-insurance" type="date" value={form.insurance_expiry || ""} onChange={(e) => set("insurance_expiry", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Last service date</Label><Input type="date" value={form.last_service_date || ""} onChange={(e) => set("last_service_date", e.target.value)} /></div>
          </div>

          <div className="rounded-tenant-card border border-tenant-border p-3">
            <p className="mb-2 text-sm font-semibold">Battery & charger — initial condition</p>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="vehicle-battery-number" placeholder="Battery number" value={form.battery_number} onChange={(e) => set("battery_number", e.target.value)} />
              <Input data-testid="vehicle-charger-number" placeholder="Charger number" value={form.charger_number} onChange={(e) => set("charger_number", e.target.value)} />
              <FileUpload testid="vehicle-battery-image" label="Battery photo" category="condition" value={form.battery_image} onChange={(id) => set("battery_image", id)} />
              <FileUpload testid="vehicle-charger-image" label="Charger photo" category="condition" value={form.charger_image} onChange={(id) => set("charger_image", id)} />
            </div>
          </div>

          <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="vehicle-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Scratches, accessories, condition notes…" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="vehicle-submit" onClick={submit} disabled={saving} className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "edit" ? "Save changes" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwapDialog({ vehicle, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ component: "battery", new_number: "", new_image: null, reason: "" });
  const submit = async () => {
    if (!f.reason.trim()) return toast.error("A reason for the swap is required");
    setSaving(true);
    try {
      await api.post(`/operator/vehicles/${vehicle.id}/swap-component`, f);
      toast.success(`${f.component} swapped`); setOpen(false);
      setF({ component: "battery", new_number: "", new_image: null, reason: "" }); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`swap-vehicle-${vehicle.reg_number}`} variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs"><Repeat className="h-3.5 w-3.5" /> Swap</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Swap component · {vehicle.reg_number}</DialogTitle>
          <DialogDescription>Record a battery or charger swap with a reason for the audit log.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Component</Label>
            <Select value={f.component} onValueChange={(v) => setF({ ...f, component: v })}>
              <SelectTrigger data-testid="swap-component"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="battery">Battery</SelectItem><SelectItem value="charger">Charger</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>New {f.component} number</Label><Input data-testid="swap-new-number" value={f.new_number} onChange={(e) => setF({ ...f, new_number: e.target.value })} placeholder={`New ${f.component} serial`} /></div>
          <div className="space-y-1.5"><Label>New {f.component} photo</Label><FileUpload testid="swap-new-image" label={`${f.component} photo`} category="condition" value={f.new_image} onChange={(id) => setF({ ...f, new_image: id })} /></div>
          <div className="space-y-1.5"><Label>Reason for swap *</Label><Input data-testid="swap-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Battery health below 70%, charger faulty…" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="swap-submit" onClick={submit} disabled={saving} className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Log swap</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { ctx, reload } = useOperator();
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const isOwner = user?.role === "owner";
  const isStaff = isOwner || user?.role === "counter_staff";

  useEffect(() => {
    api.get("/operator/telematics/providers").then(({ data }) => setProviders(data.providers)).catch(() => {});
  }, []);

  if (!ctx) return null;

  const changeProvider = async (vehicleId, provider) => {
    try {
      await api.patch(`/operator/vehicles/${vehicleId}/telematics`, { provider });
      await reload();
      toast.success(`Provider switched to ${provider}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byStatus = ctx.stats.by_status || {};
  const visibleFeatures = ctx.features.filter((f) => f.feature_key !== "credibility_score");

  const toggleFeature = async (key, enabled) => {
    try {
      await api.patch("/operator/features", { feature_key: key, enabled });
      await reload();
      toast.success(`${key.replace(/_/g, " ")} ${enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["available", "Available"], ["rented", "Rented"], ["service", "In service"], ["recovery", "Recovery"]].map(([key, label]) => (
          <div key={key} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight">{byStatus[key] || 0}</p>
          </div>
        ))}
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your fleet <span className="ml-2 text-sm font-normal text-muted-foreground">{ctx.stats.total} vehicles</span></h2>
          {isOwner && <VehicleDialog mode="add" onDone={reload} />}
        </div>
        <div data-testid="fleet-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ctx.vehicles.map((v) => (
            <div key={v.id} data-testid={`vehicle-${v.reg_number}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft transition-transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div><p className="font-semibold">{v.reg_number}</p><p className="text-sm text-muted-foreground">{v.model}{v.color ? ` · ${v.color}` : ""}{v.model_year ? ` · ${v.model_year}` : ""}</p></div>
                <StatusBadge status={v.status} />
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                <span>₹{(v.vehicle_value / 1000).toFixed(0)}k value</span>
                <span>{v.total_km?.toLocaleString()} km</span>
              </div>
              {(v.battery_number || v.charger_number || v.insurance_expiry) && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  {v.battery_number && <span>Bat #{v.battery_number}</span>}
                  {v.charger_number && <span>Chg #{v.charger_number}</span>}
                  {v.insurance_expiry && <span>Ins. {v.insurance_expiry}</span>}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-tenant-border pt-2.5">
                <Link to={`/app/vehicle/${v.id}/history`} data-testid={`vehicle-history-${v.reg_number}`}
                  className="inline-flex items-center gap-1 rounded-tenant-button px-2 py-1 text-xs font-medium text-tenant-primary hover:bg-tenant-primary/10">
                  <History className="h-3.5 w-3.5" /> History
                </Link>
                {isOwner && <VehicleDialog mode="edit" vehicle={v} onDone={reload} />}
                {isStaff && <SwapDialog vehicle={v} onDone={reload} />}
              </div>
              {isOwner && providers.length > 0 && (
                <div className="mt-2 flex items-center gap-2 border-t border-tenant-border pt-2.5">
                  <span className="text-xs text-muted-foreground">Telematics</span>
                  <select data-testid={`provider-select-${v.reg_number}`} value={v.telematics_provider || "mock"}
                    onChange={(e) => changeProvider(v.id, e.target.value)}
                    className="ml-auto rounded-md border border-input bg-background px-2 py-1 text-xs capitalize">
                    {providers.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Capabilities</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleFeatures.map((f) => (
            <div key={f.feature_key} data-testid={`capability-${f.feature_key}`}
              className={`rounded-tenant-card border p-4 shadow-tenant-soft ${f.enabled ? "border-tenant-primary/40 bg-tenant-primary/5" : "border-tenant-border bg-tenant-surface"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {!f.in_plan && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className="text-sm font-medium capitalize">{f.feature_key.replace(/_/g, " ")}</span>
                </div>
                <Switch data-testid={`toggle-${f.feature_key}`} checked={f.enabled}
                  disabled={!f.in_plan || !isOwner} onCheckedChange={(v) => toggleFeature(f.feature_key, v)} />
              </div>
              {!f.in_plan && <p className="mt-1.5 text-xs text-muted-foreground">Upgrade plan to unlock</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
